<script setup lang="ts" name="FilterCustomPeriodSelect">
import { computed, ref, watch } from 'vue';
import { useVModel } from '@vueuse/core';

import { CUSTOM_RANGE_OPTIONS } from '~/constants';

import { sendDateFilterEvent } from '~/features/useAnalytics';
import { getYesterday } from '~/features/useDates';
import { isEmpty } from '~/features/useUtilities';

import BaseButton from '~/components/BaseButton.vue';
import BaseDatePicker from '~/components/BaseDatePicker.vue';
import FilterMenuTrigger from '~/components/FilterMenuTrigger.vue';
import XDropdown from '~/components/XDropdown.vue';

import type { IGaStatisticsPage, IPeriod } from '~/types';

const props = withDefaults(
  defineProps<{
    title?: string;
    modelValue?: IPeriod;
    page?: IGaStatisticsPage;
    minDate?: Date | null;
    maxDate?: Date | null;
  }>(),
  {
    title: 'Date',
    minDate: () => new Date('2017/05/01'),
    maxDate: () => getYesterday(),
  },
);

const emit = defineEmits<{
  (e: 'update:modelValue', period?: IPeriod): void;
}>();

const period = useVModel(props, 'modelValue', emit);

const hasSelected = computed(
  () => !isEmpty(period.value?.cp_start) && !isEmpty(period.value?.cp_end),
);

const customRange = ref({
  cp_start: props.modelValue?.cp_start
    ? props.modelValue?.cp_start
    : getYesterday(),
  cp_end: props.modelValue?.cp_end ? props.modelValue?.cp_end : getYesterday(),
});

const resetCustomRangeValues = (close: () => void) => {
  period.value = {} as IPeriod;
  customRange.value.cp_start = getYesterday();
  customRange.value.cp_end = getYesterday();
  close();
};

const apply = (close: () => void) => {
  period.value = {
    id: CUSTOM_RANGE_OPTIONS.CP,
    name: 'Custom range',
    cp_start: customRange.value.cp_start.toISOString(),
    cp_end: customRange.value.cp_end.toISOString(),
  };
  close();
};

watch(
  period,
  (value) => {
    if (isEmpty(value)) {
      customRange.value.cp_start = getYesterday();
      customRange.value.cp_end = getYesterday();
    } else if (value?.name && !!props.page) {
      sendDateFilterEvent(value.name, props.page);
    }
  },
  {
    deep: true,
  },
);
</script>

<template>
  <XDropdown>
    <template #trigger="{ toggle, isOpen }">
      <FilterMenuTrigger
        icon="mdi:calendar-blank-outline"
        :is-open="isOpen"
        :has-selected="hasSelected"
        @click="toggle"
      >
        {{ title }}
      </FilterMenuTrigger>
    </template>

    <template #default="{ close }">
      <div class="p-4">
        <BaseDatePicker
          v-model="customRange"
          labelFrom="From"
          labelTo="To"
          range
          :maxDate="maxDate"
          :minDate="minDate"
        />

        <div class="mt-4 flex justify-end">
          <BaseButton
            variant="btn-tertiary"
            @click="resetCustomRangeValues(close)"
          >
            Clear
          </BaseButton>

          <div class="w-40">
            <BaseButton class="w-full" @click="apply(close)">
              Apply
            </BaseButton>
          </div>
        </div>
      </div>
    </template>
  </XDropdown>
</template>
